const ECOMMERCE_FIELDS = [
    'transaction_id',
    'currency',
    'value',
    'shipping',
    'coupon',
    'shipping_tier',
    'payment_type',
    'items',
    'products',
]

const EVENT_ALIASES = {
    view_content: 'view_item',
}

const toNumber = (value) => {
    if (value == null || value === '') {
        return value
    }

    return Number(value)
}

const normalizeItem = (item = {}) => ({
    ...item,
    item_id: item.item_id ?? item.id,
    item_name: item.item_name ?? item.name,
    item_brand: item.item_brand ?? item.brand,
    item_category: item.item_category ?? item.category,
    item_variant: item.item_variant ?? item.variant,
    item_image: item.item_image ?? item.image ?? item.image_url ?? item.image_main,
    quantity: toNumber(item.quantity),
    price: toNumber(item.price),
})

const normalizeEcommerce = (ecommerce = {}) => {
    const items = ecommerce.items ?? ecommerce.products

    return {
        ...ecommerce,
        items: Array.isArray(items) ? items.map(normalizeItem) : items,
        value: toNumber(ecommerce.value),
        shipping: toNumber(ecommerce.shipping),
    }
}

const pickEcommerceFields = (payload = {}) => {
    const ecommerce = {}

    ECOMMERCE_FIELDS.forEach((field) => {
        if (payload[field] != null) {
            ecommerce[field] = payload[field]
            delete payload[field]
        }
    })

    return ecommerce
}

export const buildCartTrackingItems = (items = []) => (items || []).map((item = {}) => {
    const itemId = item.api_cod_original ?? item.item_id ?? item.id ?? ''

    return {
        id: itemId,
        item_id: itemId,
        sku: itemId,
        name: item.pro_name_site ?? item.item_name ?? item.name ?? '',
        item_name: item.pro_name_site ?? item.item_name ?? item.name ?? '',
        brand: item.brand_name ?? item.item_brand ?? item.brand ?? '',
        item_brand: item.brand_name ?? item.item_brand ?? item.brand ?? '',
        category: item.cat_name ?? item.item_category ?? item.category ?? '',
        item_category: item.cat_name ?? item.item_category ?? item.category ?? '',
        discount: item.percentage_discount ?? item.discount ?? 0,
        quantity: Number(item.qtd_cart ?? item.quantity ?? 1),
        price: Number(item.price_to ?? item.pro_price_site ?? item.price ?? 0),
        variant: item.items_grid_abbreviaton ??
            `${item.grid_group_column_value ?? ''}${item.grid_group_row_value ?? ''}`,
        item_image: item.image_main ?? item.item_image ?? item.image ?? '',
    }
})

export const buildTrackingEvent = (eventName, payload = {}) => {
    const normalizedEventName = EVENT_ALIASES[eventName] ?? eventName
    const dataLayerEvent = { event: normalizedEventName }
    const eventPayload = { ...payload }
    const pageTitle = eventPayload.page_title ?? eventPayload.pageTitle
    const pageCategory = eventPayload.page_category ?? eventPayload.pageCategory

    delete eventPayload.page_title
    delete eventPayload.page_category
    delete eventPayload.pageTitle
    delete eventPayload.pageCategory

    if (pageTitle != null) {
        dataLayerEvent.page_title = pageTitle
    }

    if (pageCategory != null) {
        dataLayerEvent.page_category = pageCategory
    }

    const rootEcommerce = pickEcommerceFields(eventPayload)
    const ecommercePayload = eventPayload.ecommerce

    if (Object.keys(rootEcommerce).length) {
        dataLayerEvent.ecommerce = normalizeEcommerce(rootEcommerce)
    }

    if (ecommercePayload && typeof ecommercePayload === 'object') {
        dataLayerEvent.ecommerce = normalizeEcommerce(ecommercePayload)
    }

    delete eventPayload.ecommerce

    return {
        ...dataLayerEvent,
        ...eventPayload,
    }
}

export const trackDataLayerEvent = (eventName, payload = {}) => {
    window.dataLayer = window.dataLayer || []

    const dataLayerEvent = buildTrackingEvent(eventName, payload)

    if (dataLayerEvent.ecommerce) {
        window.dataLayer.push({ ecommerce: null })
    }

    window.dataLayer.push(dataLayerEvent)

    return dataLayerEvent
}

/**
 * O Pixel da Meta so entra no HTML quando ha consentimento de publicidade (ver
 * App\Helpers\CookieConsentHelper). Sem consentimento o script nao carrega e
 * `fbq` nao existe -- mas uma duzia de componentes chama fbq() direto, cinco
 * deles com await, entao a ausencia viraria ReferenceError no meio de acoes
 * como adicionar ao carrinho.
 *
 * O fallback e no-op de proposito: sem consentimento nao se rastreia nada, e
 * tambem nao se quebra nada. Quando o Pixel carrega, ele define window.fbq no
 * <head>, antes deste bundle rodar -- por isso a checagem de tipo abaixo, que
 * nunca sobrescreve o fbq de verdade.
 */
const installPixelFallback = () => {
    if (typeof window.fbq === 'function') {
        return
    }

    const fallbackFbq = function () {}

    fallbackFbq.isFflipFallback = true
    window.fbq = fallbackFbq
}

export const installTrackingFallback = () => {
    window.fflipTrackDataLayerEvent = trackDataLayerEvent
    // Chamado de novo por public/assets/js/cookie-consent.js quando o aceite de
    // cookies ativa as tags sem recarregar a pagina.
    window.fflipInstallTrackingFallback = installTrackingFallback

    // Antes do bloco do gtag: aquele tem returns antecipados.
    installPixelFallback()

    if (typeof window.gtag === 'function') {
        if (window.gtag.isFflipWrapped || window.gtag.isFflipFallback) {
            return
        }

        const originalGtag = window.gtag
        const wrappedGtag = function(command, eventName, payload = {}) {
            if (command === 'event' && eventName) {
                trackDataLayerEvent(eventName, payload)
            }

            return originalGtag.apply(this, arguments)
        }

        wrappedGtag.isFflipWrapped = true
        window.gtag = wrappedGtag
        return
    }

    const fallbackGtag = (command, eventName, payload = {}) => {
        if (command !== 'event' || !eventName) {
            return
        }

        trackDataLayerEvent(eventName, payload)
    }

    fallbackGtag.isFflipFallback = true
    window.gtag = fallbackGtag
}

const getCurrentPagePath = () => `${window.location.pathname}${window.location.search}`

export const trackPageView = () => trackDataLayerEvent('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: getCurrentPagePath(),
})

export const installPageTracking = () => {
    if (window.__fflipPageTrackingInstalled) {
        return
    }

    window.__fflipPageTrackingInstalled = true

    let currentPath = getCurrentPagePath()
    trackPageView()

    const trackWhenPathChanges = () => {
        const nextPath = getCurrentPagePath()

        if (nextPath === currentPath) {
            return
        }

        currentPath = nextPath
        trackPageView()
    }

    const dispatchHistoryChange = () => {
        window.dispatchEvent(new Event('fflip:historychange'))
    }

    const patchHistoryMethod = (method) => {
        const originalMethod = window.history[method]

        if (typeof originalMethod !== 'function') {
            return
        }

        window.history[method] = function(...args) {
            const result = originalMethod.apply(this, args)
            dispatchHistoryChange()
            return result
        }
    }

    patchHistoryMethod('pushState')
    patchHistoryMethod('replaceState')

    const handleNavigationChange = () => {
        // Espera o ciclo atual para capturar title/path já atualizados.
        setTimeout(trackWhenPathChanges, 0)
    }

    window.addEventListener('popstate', handleNavigationChange)
    window.addEventListener('hashchange', handleNavigationChange)
    window.addEventListener('fflip:historychange', handleNavigationChange)
}
